import React, { useState, useEffect, useRef } from 'react';
import {
  HardDrive,
  ShieldCheck,
  Activity,
  Search,
  FileCode2,
  CheckCircle2,
  PlaySquare,
  Terminal,
  Waypoints,
  SlidersHorizontal,
  Sun,
  MoonStar,
  LogIn,
  LogOut,
  UserCircle,
  BookOpen,
  Home,
  Download,
  ChevronDown,
  Menu,
  X,
  Building2,
  Sparkles,
  Code2,
  Crown,
  ShieldAlert,
  Eye,
  Check
} from 'lucide-react';
import { AuthSession, TenantWorkspace } from '../types';

interface NavbarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isDark: boolean;
  setIsDark: (dark: boolean) => void;
  session: AuthSession | null;
  onSignIn: () => void;
  onSignOut: () => void;
  onSwitchRole?: (role: 'owner' | 'admin' | 'operator' | 'viewer') => void;
  tenants?: TenantWorkspace[];
  activeTenant?: TenantWorkspace | null;
  onSwitchTenant?: (tenant: TenantWorkspace) => void;
}

export const roleNavPermissions: Record<string, {
  label: string;
  shortLabel: string;
  icon: string;
  roleIcon: React.ComponentType<{ className?: string }>;
  tagline: string;
  desc: string;
  badge: string;
  allowedSafetyStages: string[];
  allowedOperations: string[];
  allowedResources: string[];
  accessibleSummary: string[];
  restrictedSummary: string[];
}> = {
  admin: {
    label: 'AI Administrator',
    shortLabel: 'AI Admin',
    icon: '🛡️',
    roleIcon: ShieldAlert,
    tagline: 'Fleet Governance & Safety Enforcement',
    desc: 'Full authority over custom guardrails, agent quarantine & cold storage archives.',
    badge: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 border-emerald-300 dark:border-emerald-500/30',
    allowedSafetyStages: ['review', 'profiles', 'validate', 'simulate', 'observability'],
    allowedOperations: ['policies', 'integrations'],
    allowedResources: ['docs', 'guide', 'stats', 'api', 'enterprise', 'account', 'changelog'],
    accessibleSummary: [
      'Interactive in-browser agent reasoning sandbox (/playground)',
      'Global guardrail creation, editing & dry-run tester (/policies)',
      '1-Click agent quarantine & incident resolution (/dashboard)',
      'Cloudflare R2 cold evidence archival & retention policies (/integrations)',
      'Pre-release behavioral validation & safety scoring (/validate)'
    ],
    restrictedSummary: [
      'Organization root billing management requires Workspace Owner'
    ]
  },
  operator: {
    label: 'AI Developer / Operator',
    shortLabel: 'Developer',
    icon: '💻',
    roleIcon: Code2,
    tagline: 'Agent Development, Testing & API Integration',
    desc: 'Builds safety profiles, runs pre-flight validation gates & generates API keys.',
    badge: 'bg-blue-50 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300 border-blue-300 dark:border-blue-500/30',
    allowedSafetyStages: ['review', 'profiles', 'validate', 'simulate'],
    allowedOperations: ['lab', 'integrations'],
    allowedResources: ['docs', 'guide', 'stats', 'api', 'changelog'],
    accessibleSummary: [
      'Interactive in-browser agent reasoning sandbox (/playground)',
      'Natural language risk intake & threat modeling (/review)',
      'Safety boundary authoring & tool limits configuration (/profiles)',
      '12-check automated behavioral test gates (/validate)',
      'Live action simulator & side-by-side testing lab (/simulate, /lab)',
      'Application registration & scoped API key generation (/integrations)'
    ],
    restrictedSummary: [
      'Global policy authoring hidden (Admin governance only)',
      'Sub-tenant provisioning & member RBAC hidden (Owner/Admin only)',
      'Executive compliance certifications omitted'
    ]
  },
  owner: {
    label: 'Workspace Owner',
    shortLabel: 'Owner',
    icon: '👔',
    roleIcon: Crown,
    tagline: 'Executive Governance, RBAC & Compliance Oversight',
    desc: 'Provisions sub-tenants, manages team RBAC roles & exports audit certificates.',
    badge: 'bg-indigo-50 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300 border-indigo-300 dark:border-indigo-500/30',
    allowedSafetyStages: ['observability'],
    allowedOperations: ['policies'],
    allowedResources: ['docs', 'guide', 'stats', 'enterprise', 'account', 'changelog'],
    accessibleSummary: [
      'Sub-tenant workspace provisioning & compliance framework selection (/account)',
      'Team member invitations & RBAC role assignments (/account)',
      'NIST AI RMF and SOC 2-mapped evidence bundle downloads (/observability)',
      'Executive fleet posture oversight & policy review (/dashboard, /policies)'
    ],
    restrictedSummary: [
      'Developer test sandboxes & simulator scratchpads omitted for executive clarity'
    ]
  },
  viewer: {
    label: 'Read-Only Auditor',
    shortLabel: 'Auditor',
    icon: '👁️',
    roleIcon: Eye,
    tagline: 'Independent Security Audit & Evidence Inspection',
    desc: 'Inspects telemetry traces, DAG execution graphs & cryptographic payload hashes.',
    badge: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700',
    allowedSafetyStages: ['profiles', 'observability'],
    allowedOperations: ['policies'],
    allowedResources: ['docs', 'guide', 'stats', 'enterprise', 'changelog'],
    accessibleSummary: [
      'Multi-agent trace DAG & event stream inspection (/observability)',
      'Cryptographic SHA-256 evidence payload verification (/observability)',
      'Read-only safety profile & policy inspection (/profiles, /policies)',
      'Signed Compliance Package report exports (/observability)'
    ],
    restrictedSummary: [
      '100% Read-Only mode: All save, create, delete, quarantine & release buttons are hidden or disabled'
    ]
  }
};

export const Navbar: React.FC<NavbarProps> = ({ 
  activeTab, 
  setActiveTab, 
  isDark, 
  setIsDark, 
  session, 
  onSignIn, 
  onSignOut,
  onSwitchRole,
  tenants = [],
  activeTenant,
  onSwitchTenant
}) => {
  const [showSafetyMenu, setShowSafetyMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [showTenantMenu, setShowTenantMenu] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  // Close dropdowns on click outside or escape key
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setShowSafetyMenu(false);
        setShowMoreMenu(false);
        setShowUserMenu(false);
        setShowRoleMenu(false);
        setShowTenantMenu(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowSafetyMenu(false);
        setShowMoreMenu(false);
        setShowUserMenu(false);
        setShowRoleMenu(false);
        setShowTenantMenu(false);
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const tenantList = tenants.length > 0 ? tenants : [{ id: 'org-demo-agenticscale', slug: 'demo-agenticscale', name: 'AgenticScale Demo Organization', environment: 'sandbox' as const, compliance_framework: 'OWASP Top 10', agent_count: 4, open_incidents: 0, created_at: '2026-01-01T00:00:00Z' }];
  const currentTenant = activeTenant || tenantList.find(t => t.id === session?.organization?.id) || tenantList[0];
  const currentRole = session?.organization?.role || 'admin';
  const activeRoleDetails = roleNavPermissions[currentRole] || roleNavPermissions.admin;

  const safetySuiteItems = [
    { id: 'review', label: '1. Assess the agent', stage: 'Step 1', desc: 'What it can reach, and how much damage it could do', icon: Search },
    { id: 'profiles', label: '2. Approve the boundaries', stage: 'Step 2', desc: 'A person accepts the limits, by name, before any code runs', icon: FileCode2 },
    { id: 'validate', label: '3. Test before release', stage: 'Step 3', desc: '12 deterministic pre-flight checks', icon: CheckCircle2 },
    { id: 'simulate', label: '4. Enforce at runtime', stage: 'Step 4', desc: 'Allow, hold for a human, or block — with the reason', icon: PlaySquare },
    { id: 'observability', label: '5. Keep the evidence', stage: 'Step 5', desc: 'Every decision recorded with a hash of what was decided', icon: Waypoints }
  ];

  const operationsItems = [
    { id: 'policies', label: 'Policy Registry', desc: 'The rules the engine applies, and a dry-run tester', icon: SlidersHorizontal }
  ];

  const resourceItems = [
    { id: 'download', label: 'Download and run it yourself', desc: 'The whole platform on your machine — one archive, no account', icon: HardDrive },
    { id: 'verify', label: 'Verify every claim', desc: 'What is proven, what is self-measured, what is not true yet', icon: ShieldCheck },
    { id: 'dataset', label: 'Failure dataset', desc: '108 real incidents, each linked to the filing or postmortem that reported it', icon: BookOpen },
    { id: 'docs', label: 'Documentation', desc: 'Architecture, guides, quickstarts and specifications', icon: BookOpen },
    { id: 'api', label: 'API & SDK reference', desc: 'HTTP contract, MCP tools, Python and TypeScript clients', icon: Terminal }
  ];

  // Role Filtering
  const visibleSafetyItems = safetySuiteItems.filter(item => 
    activeRoleDetails.allowedSafetyStages.includes(item.id)
  );
  const visibleOperationsItems = operationsItems.filter(item => 
    activeRoleDetails.allowedOperations.includes(item.id)
  );
  const visibleResourceItems = resourceItems.filter(item => 
    activeRoleDetails.allowedResources.includes(item.id)
  );

  const isSafetySuiteActive = visibleSafetyItems.some(i => i.id === activeTab);
  const isMoreActive = [...visibleOperationsItems, ...visibleResourceItems].some(i => i.id === activeTab);

  const closeAllMenus = () => {
    setShowSafetyMenu(false);
    setShowMoreMenu(false);
    setShowUserMenu(false);
    setShowRoleMenu(false);
    setShowTenantMenu(false);
    setShowMobileMenu(false);
  };

  const navigate = (tab: string) => {
    setActiveTab(tab);
    closeAllMenus();
  };

  const handleRoleSelection = (roleKey: 'owner' | 'admin' | 'operator' | 'viewer') => {
    if (onSwitchRole) onSwitchRole(roleKey);
    setShowRoleMenu(false);

    const targetPerms = roleNavPermissions[roleKey];
    const allAllowed = [
      'guide',
      'dashboard',
      ...targetPerms.allowedSafetyStages,
      ...targetPerms.allowedOperations,
      ...targetPerms.allowedResources
    ];
    if (!allAllowed.includes(activeTab)) {
      setActiveTab('dashboard');
    }
  };

  return (
    <header ref={navRef} className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-md transition-colors dark:border-slate-800 dark:bg-slate-950/95">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 2xl:max-w-shell">
        {/* No overflow clipping on this row. Every dropdown below is absolutely
            positioned inside it, so `overflow-hidden` here hides the menus
            entirely. The row is kept inside its bounds by letting the left
            cluster shrink instead: the logo holds its size, the link nav gives
            up its gaps first, and the heavier controls only appear at widths
            that have been measured to hold them. */}
        <div className="flex h-16 min-w-0 items-center justify-between gap-3">
          
          {/* ================= LEFT: Logo & Primary Desktop Nav ================= */}
          <div className="flex min-w-0 items-center gap-3 lg:gap-5">
            
            {/* Logo */}
            <button
              type="button"
              className="flex shrink-0 cursor-pointer items-center space-x-2 text-left group"
              onClick={() => navigate('home')}
              aria-label="AgenticScale home"
            >
              <div className="p-1.5 rounded-xl bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 group-hover:scale-105 transition-transform">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <span className="text-lg font-black tracking-tight text-slate-900 dark:text-white whitespace-nowrap">
                AgenticScale
              </span>
            </button>

            {/* Desktop Navigation Links (Hidden on < lg to prevent squeeze) */}
            <nav className="hidden xl:flex items-center gap-1.5 shrink-0" aria-label="Primary navigation">

              {/* Home. The wordmark goes here too, but a visitor who has walked
                  into the dashboard has no obvious way back to the page that
                  explains what any of this is. */}
              <button
                type="button"
                onClick={() => navigate('home')}
                className={`inline-flex h-10 items-center gap-2 rounded-xl px-3.5 text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === 'home'
                    ? 'border border-brand-200 bg-brand-50 text-brand-700 shadow-2xs dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                }`}
              >
                <Home className="h-4 w-4" />
                <span>Home</span>
              </button>

              {/* Why this exists — the plain-language explanation. This is the
                  first thing most visitors need, so it is top level rather than
                  inside a submenu. The older visual lifecycle guide is still at
                  /guide for anyone who wants it. */}
              <button
                type="button"
                onClick={() => navigate('method')}
                className={`inline-flex h-10 items-center gap-2 rounded-xl px-3.5 text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === 'method'
                    ? 'border border-brand-200 bg-brand-50 text-brand-700 shadow-2xs dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                }`}
              >
                <BookOpen className="h-4 w-4" />
                <span>Why this exists</span>
              </button>

              {/* Dashboard */}
              <button
                type="button"
                onClick={() => navigate('dashboard')}
                className={`inline-flex h-10 items-center gap-2 rounded-xl px-3.5 text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === 'dashboard'
                    ? 'border border-brand-200 bg-brand-50 text-brand-700 shadow-2xs dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                }`}
              >
                <Activity className="h-4 w-4" />
                <span>Dashboard</span>
              </button>

              {/* Safety Suite Dropdown */}
              {visibleSafetyItems.length > 0 && (
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSafetyMenu(!showSafetyMenu);
                      setShowMoreMenu(false);
                      setShowRoleMenu(false);
                      setShowTenantMenu(false);
                      setShowUserMenu(false);
                    }}
                    className={`inline-flex h-10 items-center gap-2 rounded-xl px-3.5 text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                      isSafetySuiteActive
                        ? 'border border-brand-200 bg-brand-50 text-brand-700 shadow-2xs dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                    }`}
                    aria-expanded={showSafetyMenu}
                  >
                    <Sparkles className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                    <span>Safety Suite</span>
                    <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${showSafetyMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {showSafetyMenu && (
                    <div className="absolute left-0 top-12 z-50 w-84 sm:w-[420px] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl dark:border-slate-800 dark:bg-slate-900 animate-fade-in">
                      <div className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-800 mb-2 flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Continuous Safety Lifecycle</span>
                        <span className="text-[10px] font-mono text-brand-600 dark:text-brand-400 font-semibold">{visibleSafetyItems.length} Stages for {activeRoleDetails.shortLabel}</span>
                      </div>

                      <div className="space-y-1">
                        {visibleSafetyItems.map((item) => {
                          const Icon = item.icon;
                          const isCurrent = activeTab === item.id;
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => navigate(item.id)}
                              className={`w-full p-2.5 rounded-xl text-left transition flex items-start justify-between gap-3 cursor-pointer ${
                                isCurrent
                                  ? 'bg-brand-50 dark:bg-brand-500/15 border border-brand-200 dark:border-brand-500/30 text-brand-900 dark:text-brand-200'
                                  : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
                              }`}
                            >
                              <div className="flex items-start gap-3 min-w-0">
                                <div className={`p-2 rounded-lg shrink-0 mt-0.5 ${isCurrent ? 'bg-brand-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                  <Icon className="h-4 w-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-bold">{item.label}</span>
                                    <span className="text-[9px] px-1.5 py-0.2 rounded font-mono font-bold uppercase bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                                      {item.stage}
                                    </span>
                                  </div>
                                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{item.desc}</p>
                                </div>
                              </div>
                              {isCurrent && <Check className="h-4 w-4 text-brand-600 dark:text-brand-400 shrink-0 mt-1" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Operations & Docs Dropdown */}
              {(visibleOperationsItems.length > 0 || visibleResourceItems.length > 0) && (
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(!showMoreMenu);
                      setShowSafetyMenu(false);
                      setShowRoleMenu(false);
                      setShowTenantMenu(false);
                      setShowUserMenu(false);
                    }}
                    className={`inline-flex h-10 items-center gap-2 rounded-xl px-3.5 text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                      isMoreActive
                        ? 'border border-brand-200 bg-brand-50 text-brand-700 shadow-2xs dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                    }`}
                    aria-expanded={showMoreMenu}
                  >
                    <span>Operations & Docs</span>
                    <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${showMoreMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {showMoreMenu && (
                    <div className="absolute left-0 top-12 z-50 w-84 sm:w-[420px] rounded-2xl border border-slate-200 bg-white p-3.5 shadow-2xl dark:border-slate-800 dark:bg-slate-900 animate-fade-in">
                      {visibleOperationsItems.length > 0 && (
                        <>
                          <p className="px-2.5 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Governance & Security Operations</p>
                          <div className="space-y-1">
                            {visibleOperationsItems.map((item) => {
                              const Icon = item.icon;
                              const isCurrent = activeTab === item.id;
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => navigate(item.id)}
                                  className={`w-full p-2.5 rounded-xl text-left transition flex items-start gap-3 cursor-pointer ${
                                    isCurrent
                                      ? 'bg-brand-50 dark:bg-brand-500/15 border border-brand-200 dark:border-brand-500/30 text-brand-900 dark:text-brand-200'
                                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
                                  }`}
                                >
                                  <div className={`p-1.5 rounded-lg shrink-0 mt-0.5 ${isCurrent ? 'bg-brand-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                    <Icon className="h-4 w-4" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="text-xs font-bold block">{item.label}</span>
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{item.desc}</p>
                                  </div>
                                  {isCurrent && <Check className="h-4 w-4 text-brand-600 dark:text-brand-400 shrink-0 mt-1" />}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}

                      {visibleOperationsItems.length > 0 && visibleResourceItems.length > 0 && (
                        <div className="my-2.5 border-t border-slate-100 dark:border-slate-800" />
                      )}

                      {visibleResourceItems.length > 0 && (
                        <>
                          <p className="px-2.5 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Documentation & Knowledge Hub</p>
                          <div className="space-y-1">
                            {visibleResourceItems.map((item) => {
                              const Icon = item.icon;
                              const isCurrent = activeTab === item.id;
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => navigate(item.id)}
                                  className={`w-full p-2 rounded-xl text-left transition flex items-center justify-between gap-3 text-xs font-semibold cursor-pointer ${
                                    isCurrent
                                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 font-bold'
                                      : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/60'
                                  }`}
                                >
                                  <span className="flex items-center gap-2.5">
                                    <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                                    <span>{item.label}</span>
                                  </span>
                                  {isCurrent && <Check className="h-4 w-4 text-brand-600 dark:text-brand-400 shrink-0" />}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

            </nav>
          </div>

          {/* ================= RIGHT: Controls (Desktop & Mobile Adaptive) ================= */}
          <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">

            {/* Tenant Workspace Selector (Desktop only: hidden on < lg to prevent crowding) */}
            <div className="relative hidden 2xl:block shrink-0">
              <button
                type="button"
                onClick={() => {
                  setShowTenantMenu(!showTenantMenu);
                  setShowRoleMenu(false);
                  setShowSafetyMenu(false);
                  setShowMoreMenu(false);
                  setShowUserMenu(false);
                }}
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-xs font-bold text-slate-800 dark:text-slate-200 transition-all shadow-2xs hover:border-brand-300 whitespace-nowrap cursor-pointer"
                title="Switch Tenant Workspace"
                aria-expanded={showTenantMenu}
              >
                <Building2 className="h-4 w-4 text-brand-600 dark:text-brand-400 shrink-0" />
                <span className="max-w-[130px] xl:max-w-[170px] truncate">
                  {currentTenant.name.replace(/\s*\([^)]*\)/g, '')}
                </span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono font-bold uppercase shrink-0 ${
                  currentTenant.environment === 'production' 
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' 
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                }`}>
                  {currentTenant.environment === 'production' ? 'PROD' : 'SANDBOX'}
                </span>
                <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${showTenantMenu ? 'rotate-180' : ''}`} />
              </button>

              {showTenantMenu && (
                <div className="absolute right-0 top-12 z-50 w-84 sm:w-[440px] rounded-2xl border border-slate-200 bg-white p-3.5 shadow-2xl dark:border-slate-800 dark:bg-slate-900 animate-fade-in">
                  <div className="px-2 py-1.5 border-b border-slate-100 dark:border-slate-800 mb-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Enterprise Workspaces</span>
                      <span className="text-[10px] font-mono text-brand-600 dark:text-brand-400 font-semibold">{tenantList.length} Workspaces</span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Switch tenant to partition fleet telemetry and security policies.</p>
                  </div>

                  <div className="space-y-1.5">
                    {tenantList.map((t) => {
                      const isCurrent = t.id === currentTenant.id;
                      const cleanName = t.name.replace(/\s*\([^)]*\)/g, '');
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            if (onSwitchTenant) onSwitchTenant(t);
                            setShowTenantMenu(false);
                          }}
                          className={`w-full p-3 rounded-xl text-left transition flex items-center justify-between gap-3 cursor-pointer ${
                            isCurrent
                              ? 'bg-brand-50 dark:bg-brand-500/15 border border-brand-200 dark:border-brand-500/30'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 border border-transparent'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-bold ${isCurrent ? 'text-brand-900 dark:text-brand-200' : 'text-slate-900 dark:text-white'}`}>
                                {cleanName}
                              </span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono font-bold uppercase shrink-0 ${
                                t.environment === 'production' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                              }`}>
                                {t.environment}
                              </span>
                            </div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 font-mono flex items-center gap-2 flex-wrap">
                              <span>{t.slug}</span>
                              <span>·</span>
                              <span>{t.compliance_framework}</span>
                              <span>·</span>
                              <span>{t.agent_count} Agents</span>
                            </div>
                          </div>
                          {isCurrent && <Check className="h-4 w-4 text-brand-600 dark:text-brand-400 shrink-0 ml-1" />}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-2.5 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] px-2">
                    <span className="text-slate-400">Strict D1 Partitioning</span>
                    <button
                      type="button"
                      onClick={() => navigate('account')}
                      className="text-brand-600 dark:text-brand-400 font-bold hover:underline cursor-pointer"
                    >
                      Manage All Tenants →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Role switcher. Signed-in members only: the roles are real and
                enforced server-side, but offering a first-time visitor a
                "preview a persona" control is demo furniture that makes the
                product harder to understand, not easier. */}
            <div className={`relative shrink-0 ${session?.authenticated ? 'hidden xl:block' : 'hidden'}`}>
              <button
                type="button"
                onClick={() => {
                  setShowRoleMenu(!showRoleMenu);
                  setShowTenantMenu(false);
                  setShowSafetyMenu(false);
                  setShowMoreMenu(false);
                  setShowUserMenu(false);
                }}
                className={`inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-xs font-bold transition-all shadow-2xs whitespace-nowrap cursor-pointer ${activeRoleDetails.badge}`}
                title="Switch User Persona & Preview Features"
                aria-expanded={showRoleMenu}
              >
                <span className="text-base">{activeRoleDetails.icon}</span>
                <span>{activeRoleDetails.shortLabel}</span>
                <ChevronDown className={`h-3.5 w-3.5 opacity-60 transition-transform ${showRoleMenu ? 'rotate-180' : ''}`} />
              </button>

              {showRoleMenu && (
                <div className="absolute right-0 top-12 z-50 w-80 sm:w-96 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-2xl dark:border-slate-800 dark:bg-slate-900 animate-fade-in max-h-[85vh] overflow-y-auto">
                  <div className="px-1 py-1 border-b border-slate-100 dark:border-slate-800 mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Role-Based Feature Matrix</span>
                      <span className="text-[10px] font-mono text-brand-600 dark:text-brand-400 font-semibold">{currentTenant.slug}</span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 font-medium">
                      Select a role to preview its tailored navigation, permissions, and visible features.
                    </p>
                  </div>

                  <div className="space-y-2">
                    {(Object.keys(roleNavPermissions) as Array<'admin' | 'operator' | 'owner' | 'viewer'>).map((rKey) => {
                      const item = roleNavPermissions[rKey];
                      const isCurrent = currentRole === rKey;
                      return (
                        <div
                          key={rKey}
                          className={`p-3 rounded-xl border transition-all ${
                            isCurrent
                              ? 'bg-brand-50/50 dark:bg-brand-500/15 border-brand-300 dark:border-brand-500/40 shadow-2xs'
                              : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/30'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{item.icon}</span>
                              <div>
                                <h4 className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                                  <span>{item.label}</span>
                                  {isCurrent && (
                                    <span className="text-[9px] px-1.5 py-0.2 rounded font-bold uppercase bg-brand-600 text-white">
                                      Active
                                    </span>
                                  )}
                                </h4>
                                <p className="text-[10px] text-slate-500 dark:text-slate-400">{item.tagline}</p>
                              </div>
                            </div>

                            {!isCurrent && (
                              <button
                                type="button"
                                onClick={() => handleRoleSelection(rKey)}
                                className="px-2.5 py-1 rounded-lg text-xs font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-brand-600 hover:text-white dark:hover:bg-brand-500 text-slate-800 dark:text-slate-200 transition shrink-0 cursor-pointer"
                              >
                                Switch
                              </button>
                            )}
                          </div>

                          <div className="mt-2 space-y-1 text-[11px] text-slate-600 dark:text-slate-300">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Available Features:</p>
                            <ul className="space-y-0.5">
                              {item.accessibleSummary.slice(0, 2).map((feat, idx) => (
                                <li key={idx} className="flex items-start gap-1.5 text-[10.5px]">
                                  <span className="text-emerald-500 font-bold">✓</span>
                                  <span className="leading-tight">{feat}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-400 px-1">
                    <span>Tenant ID: <code className="font-mono">{currentTenant.id}</code></span>
                    <button
                      type="button"
                      onClick={() => navigate('account')}
                      className="text-brand-600 dark:text-brand-400 font-bold hover:underline cursor-pointer"
                    >
                      Manage Team RBAC →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Account / User Menu or Sign In (Uniform 40px height) */}
            <div className="relative shrink-0">
              {session?.authenticated ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserMenu(!showUserMenu);
                      setShowRoleMenu(false);
                      setShowTenantMenu(false);
                      setShowSafetyMenu(false);
                      setShowMoreMenu(false);
                    }}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 text-left text-xs font-bold text-slate-800 transition-all hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 cursor-pointer shadow-2xs whitespace-nowrap"
                    aria-expanded={showUserMenu}
                  >
                    <UserCircle className="h-4 w-4 text-slate-500" />
                    <span className="hidden sm:inline max-w-[85px] truncate">{session.user?.name || 'Account'}</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 top-12 z-50 w-64 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-2xl dark:border-slate-800 dark:bg-slate-900 animate-fade-in">
                      <p className="truncate text-xs font-bold text-slate-900 dark:text-white">{session.user?.email}</p>
                      <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{session.organization?.name}</p>
                      <div className="my-2.5 border-t border-slate-100 dark:border-slate-800" />
                      <button 
                        type="button" 
                        onClick={() => navigate('account')} 
                        className="flex w-full items-center gap-2 rounded-xl p-2.5 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800 cursor-pointer"
                      >
                        <UserCircle className="h-4 w-4" />
                        <span>Account & Tenants</span>
                      </button>
                      <button 
                        type="button" 
                        onClick={() => { onSignOut(); closeAllMenus(); }} 
                        className="flex w-full items-center gap-2 rounded-xl p-2.5 text-left text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/30 cursor-pointer"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Sign out</span>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                // What a visitor is here to do is run this on their own machine.
                // No sign-in here. Quiet was not enough: this deployment's
                // sign-in goes through Cloudflare Access for one organization,
                // so a visitor who pressed it met a login wall they could never
                // pass — and, worse, came away thinking the product needs an
                // account. It does not; the thing you download has no sign-in
                // at all. The capability is real and is explained where it
                // applies, under "Running it for a team", with a way in for the
                // people it is actually for.
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('download')}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-brand-600 bg-brand-600 px-4 text-xs font-bold text-white shadow-xs transition hover:bg-brand-500 cursor-pointer whitespace-nowrap"
                  >
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">Download</span>
                    <span className="sm:hidden">Get it</span>
                  </button>
                </div>
              )}
            </div>

            {/* Dark Mode Toggle (Uniform 40px height) */}
            <button
              type="button"
              onClick={() => setIsDark(!isDark)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 shadow-2xs transition-all hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 cursor-pointer"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              aria-label="Toggle Theme"
            >
              {isDark ? <Sun className="h-4 w-4 text-amber-400" /> : <MoonStar className="h-4 w-4 text-slate-600" />}
            </button>

            {/* Mobile Hamburger Button (Uniform 40px height) */}
            <button
              type="button"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="inline-flex xl:hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 shadow-2xs cursor-pointer"
              aria-expanded={showMobileMenu}
              aria-label="Open Mobile Menu"
            >
              {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

          </div>

        </div>
      </div>

      {/* ================= MOBILE DRAWER ================= */}
      {showMobileMenu && (
        <nav className="xl:hidden border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur-md px-4 py-4 space-y-4 animate-fade-in shadow-xl max-h-[85vh] overflow-y-auto">
          
          {/* Mobile Tenant & Role Quick Switchers */}
          <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400" />
                <span>{currentTenant.name}</span>
              </span>
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded font-bold uppercase bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                {currentTenant.environment}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200 dark:border-slate-800">
              <span className="text-slate-500">Active Persona:</span>
              <span className="font-bold text-brand-600 dark:text-brand-400">{activeRoleDetails.label}</span>
            </div>
          </div>

          {/* Quick links */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => navigate('home')}
              className={`p-3 rounded-xl text-left text-xs font-bold flex items-center gap-2 ${
                activeTab === 'home' ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
              }`}
            >
              <Home className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              <span>Home</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('method')}
              className={`p-3 rounded-xl text-left text-xs font-bold flex items-center gap-2 ${
                activeTab === 'method' ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
              }`}
            >
              <BookOpen className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              <span>Why this exists</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('dashboard')}
              className={`p-3 rounded-xl text-left text-xs font-bold flex items-center gap-2 ${
                activeTab === 'dashboard' ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
              }`}
            >
              <Activity className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              <span>Dashboard</span>
            </button>
          </div>

          {/* Safety Lifecycle */}
          {visibleSafetyItems.length > 0 && (
            <div>
              <p className="px-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Safety Lifecycle ({activeRoleDetails.shortLabel})</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {visibleSafetyItems.map((item) => {
                  const Icon = item.icon;
                  const isCurrent = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => navigate(item.id)}
                      className={`p-2.5 rounded-xl text-left transition flex items-center justify-between text-xs font-semibold ${
                        isCurrent
                          ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 font-bold'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Icon className="h-4 w-4 text-brand-600 dark:text-brand-400 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </div>
                      <span className="text-[9px] px-1.5 py-0.2 rounded font-mono font-bold uppercase bg-slate-100 dark:bg-slate-800 text-slate-500">
                        {item.stage}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Operations & Docs */}
          {(visibleOperationsItems.length > 0 || visibleResourceItems.length > 0) && (
            <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
              <p className="px-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Operations & Documentation</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {[...visibleOperationsItems, ...visibleResourceItems].map((item) => {
                  const Icon = item.icon;
                  const isCurrent = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => navigate(item.id)}
                      className={`p-2.5 rounded-xl text-left text-xs font-semibold flex items-center gap-2.5 ${
                        isCurrent ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 font-bold' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <Icon className="h-4 w-4 text-slate-400" />
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Below the wide breakpoint the sign-in link is not in the row, so it
              lives here — under the thing a visitor is actually here for. */}
          {!session?.authenticated && (
            <div className="border-t border-slate-200 pt-4 dark:border-slate-800">
              <button
                type="button"
                onClick={() => navigate('download')}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 p-3 text-xs font-bold text-white transition hover:bg-brand-500"
              >
                <Download className="h-4 w-4" />
                Download and run it yourself
              </button>
              <button
                type="button"
                onClick={() => { onSignIn(); closeAllMenus(); }}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl p-2.5 text-xs font-semibold text-slate-500 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                <LogIn className="h-3.5 w-3.5" />
                Operator sign-in · for whoever runs this deployment
              </button>
            </div>
          )}
        </nav>
      )}
    </header>
  );
};
